import { useState } from 'react'
import CompensationIsland from '../information-island/compensation-island/compensation-island'
import { RefundIsland } from '@/ui/components/process-details/refund-island'
import ProcessIsland from '@/ui/components/process-details/process-island/process-island'
import { CustomerInformation } from '@/ui/components/process-details/information-island/customer-information'
import { SummaryIsland } from '../summary-island'
import { Documents } from './documents'
import { UnauthorizeIsland } from '../unauthorized-island'
import { AuthorizeIsland } from '../authorize-island'
import { ReturnDocument, ReturnedItem, ReturnProcess } from '@/domain/returns'
import { Customer } from '@/domain/customer'
import { RestockItem } from '@/domain/restock'
import { NewReview } from '@/domain/review'
import { RefundMenu } from '../refund-menu/refund-menu'
import { LogisticsIsland } from '../logistics-island/logistics-island'
import {
  PbLogisticsSummary,
  PbLogisticsSummaryReturnMethodEnum
} from '@itsrever/rever-api'
import { useAppSelector } from '@/redux/hooks'

interface IslandsContainerProps {
  processDetails?: ReturnProcess
  processLogistics?: PbLogisticsSummary
  reviews: NewReview[]
  restocks: RestockItem[]
  setRestocks: (restocks: RestockItem[]) => void
  returnedItems: ReturnedItem[]
  notReceivedItems: ReturnedItem[]
  returnDocuments: ReturnDocument[]
  reviewMode: boolean
  setReviewMode: (value: boolean) => void
  restockMode: boolean
  unauthorizeMode: boolean
  setUnauthorizeMode: (value: boolean) => void
  authorizeMode: boolean
  setAuthorizeMode: (value: boolean) => void
  unauthorized: boolean
  editAmount: boolean
  setEditAmount: (value: boolean) => void
  isClaim: boolean
}

const IslandsContainer: React.FC<IslandsContainerProps> = ({
  processDetails,
  processLogistics,
  reviews,
  restocks,
  setRestocks,
  returnedItems,
  notReceivedItems,
  returnDocuments,
  reviewMode,
  setReviewMode,
  restockMode,
  unauthorizeMode,
  setUnauthorizeMode,
  authorizeMode,
  setAuthorizeMode,
  editAmount,
  setEditAmount,
  isClaim
}) => {
  const [newReviews, setNewReviews] = useState<NewReview[]>(reviews)
  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  const declinedProducts = returnedItems.filter((_, i) => {
    return (
      newReviews[i]?.status !== 'PENDING' &&
      newReviews[i]?.status !== 'APPROVED'
    )
  })

  const isInstantExchange =
    processDetails?.extraLineItems?.some(
      (item) => item.type === 'INSTANT_EXCHANGE'
    ) ?? false

  const showRefundIsland =
    reviewMode && processDetails?.isAmountEditable === true

  const renderRefundMenu = () => {
    if (!processDetails || isClaim) return null

    const isExcludedStatus =
      processDetails.status === 'CANCELLED' ||
      processDetails.status === 'NOT_AUTHORIZED'

    if (isExcludedStatus) return null

    const isGiftCardOrPromoCode =
      processDetails.refundPaymentMethod === 'GIFT_CARD' ||
      processDetails.refundPaymentMethod === 'PROMO_CODE'

    const pendingRefundAmount = parseFloat(
      processDetails?.compensation?.statusSummary?.pendingRefundAmount
        ?.amount ?? '0'
    )

    const hasNotBeenRefunded = pendingRefundAmount > 0

    // The dialog asks the backend whether the exchange can become a refund; no platform call on page load.
    const hasExchangeOrder =
      processDetails.isOnlyExchange && processDetails.exchangeOrderNumber !== ''

    return isGiftCardOrPromoCode || hasNotBeenRefunded || hasExchangeOrder ? (
      <RefundMenu process={processDetails} />
    ) : null
  }

  const isInStoreReturn =
    processLogistics?.return_method ===
    PbLogisticsSummaryReturnMethodEnum.InStore

  const automatedStoreToWarehouseEnabled =
    settings.in_store_settings
      ?.automated_store_to_warehouse_logistics_enabled ?? false

  const showLogisticsIsland =
    !processDetails?.authorizationPending &&
    !processDetails?.unauthorized &&
    processDetails?.status !== 'CANCELLED' &&
    processLogistics?.return_method &&
    processLogistics.return_method !==
      PbLogisticsSummaryReturnMethodEnum.NoReturnMethod &&
    (!isInStoreReturn || automatedStoreToWarehouseEnabled)

  return (
    <div className="relative grid grid-cols-[repeat(3,_minmax(0,_1fr))] gap-4">
      <div className="relative col-span-2 flex flex-col gap-4">
        <ProcessIsland
          processDetails={processDetails}
          restockMode={restockMode}
          reviewMode={reviewMode}
          setReviewMode={setReviewMode}
          returnedItems={returnedItems}
          notReceivedItems={notReceivedItems}
          reviews={newReviews}
          setReviews={setNewReviews}
          restocks={restocks}
          setRestocks={setRestocks}
          hasReviews={returnedItems.some((item) => item.review)}
          showRefundIsland={showRefundIsland}
          isClaim={isClaim}
          returnMethod={processLogistics?.return_method}
        />
        {returnDocuments.length ? (
          <Documents returnDocuments={returnDocuments} />
        ) : null}
      </div>
      {authorizeMode ? (
        <AuthorizeIsland
          processDetails={processDetails}
          setAuthorizeMode={setAuthorizeMode}
          editAmount={editAmount}
          setEditAmount={setEditAmount}
        />
      ) : unauthorizeMode ? (
        <UnauthorizeIsland
          processDetails={processDetails}
          setUnauthorizeMode={setUnauthorizeMode}
        />
      ) : showRefundIsland ? (
        <RefundIsland
          processDetails={processDetails}
          declinedProducts={declinedProducts}
          newReviews={newReviews}
          setReviewMode={setReviewMode}
          returnMethod={processLogistics?.return_method}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <CustomerInformation
            customer={processDetails?.customer ?? ({} as Customer)}
          />
          {showLogisticsIsland && processLogistics && (
            <LogisticsIsland processLogistics={processLogistics} />
          )}
          <CompensationIsland
            isInstantExchange={isInstantExchange}
            promoCode={processDetails?.promoCode ?? ''}
            giftCardCode={processDetails?.giftCardCode ?? ''}
            exchangeOrderNumber={processDetails?.exchangeOrderNumber ?? ''}
            exchangeOrderUrl={processDetails?.exchangeOrderUrl ?? ''}
          />
          <SummaryIsland processDetails={processDetails}>
            {renderRefundMenu()}
          </SummaryIsland>
        </div>
      )}
    </div>
  )
}

export default IslandsContainer
